// Notizen strukturiert anzeigen (Dominik 14.10.2026: „das muss schöner strukturiert werden“): Die Notizen eines Leads sind ein
// Text, an den jede Notiz mit Kopf angehängt wird („14.10.2026 11:48 · Manfred Eisenhardt zum Termin am 14.10.2026, 08:30: …“).
// Hier werden daraus Einträge (neueste zuerst) mit Abschnitten, Listen und Feldern; ältere Einträge, deren Inhalt in einem
// neueren steckt (z. B. derselbe Übergabebericht als Notiz und im Terminbericht), werden als doppelt markiert.

export type NoteBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'sub'; text: string }
  | { kind: 'field'; label: string; text: string }
  | { kind: 'item'; text: string }
  | { kind: 'text'; text: string }
  | { kind: 'rule' };

export type NoteEntry = {
  key: string;
  when: string;
  author: string;
  appointment: string;
  report: boolean;
  blocks: NoteBlock[];
  length: number;
  duplicateOf: string;
};

const header = /^(\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}) · (.+?)(?: zum Termin am (\d{2}\.\d{2}\.\d{4}, \d{2}:\d{2}))?: ?(.*)$/;
const sectionHeading = /^\d{1,2}\s*[.)]?\s+[A-ZÄÖÜ][^.:!?]{2,70}$/;
const bullet = /^[-•*–·]\s+(.+)$/;
const field = /^([A-ZÄÖÜ][\wÄÖÜäöüß /().&-]{1,32}?)\s*:\s+(.+)$/;
const rule = /^[-_=*]{4,}$/;

// lines: Zeilen ohne Ausfüll-Unterstriche, eingebettete Trennlinien („Text------Text“) als eigene Zeile.
function lines(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    // Ausfüll-Unterstriche am Zeilenende („Teilnehmer: …______“) sind keine Trennlinie.
    const parts = raw.replace(/_{3,}\s*$/, '').split(/-{5,}|_{5,}/);
    parts.forEach((p, i) => {
      if (i > 0) out.push('----');
      out.push(p.trim());
    });
  }
  return out;
}

export function noteBlocks(text: string): NoteBlock[] {
  const blocks: NoteBlock[] = [];
  for (const line of lines(text)) {
    if (!line) continue;
    if (rule.test(line)) {
      if (blocks.length && blocks[blocks.length - 1]?.kind !== 'rule') blocks.push({ kind: 'rule' });
      continue;
    }
    const b = bullet.exec(line);
    if (b?.[1]) {
      blocks.push({ kind: 'item', text: b[1] });
      continue;
    }
    if (sectionHeading.test(line)) {
      blocks.push({ kind: 'heading', text: line.replace(/^(\d{1,2})\s*[.)]?\s+/, '$1. ') });
      continue;
    }
    if (line.endsWith(':') && line.length <= 50) {
      blocks.push({ kind: 'sub', text: line.slice(0, -1) });
      continue;
    }
    const f = field.exec(line);
    if (f?.[1] && f[2]) {
      blocks.push({ kind: 'field', label: f[1].trim(), text: f[2] });
      continue;
    }
    blocks.push({ kind: 'text', text: line });
  }
  while (blocks.length && blocks[blocks.length - 1]?.kind === 'rule') blocks.pop();
  return blocks;
}

function words(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .split(/[^a-zäöüß0-9]+/)
      .filter((w) => w.length > 2),
  );
}

// parseNotes: Einträge, neueste zuerst; Text vor dem ersten Kopf (z. B. aus Dialfire) als „Frühere Notizen“.
export function parseNotes(notes: string | null | undefined): NoteEntry[] {
  const text = (notes ?? '').trim();
  if (!text) return [];
  type Raw = { when: string; author: string; appointment: string; body: string[] };
  const raw: Raw[] = [];
  let cur: Raw = { when: '', author: '', appointment: '', body: [] };
  for (const line of text.replace(/\r\n/g, '\n').split('\n')) {
    const h = header.exec(line);
    if (h) {
      if (cur.when || cur.body.some((l) => l.trim())) raw.push(cur);
      cur = { when: h[1] ?? '', author: h[2] ?? '', appointment: h[3] ?? '', body: [h[4] ?? ''] };
    } else {
      cur.body.push(line);
    }
  }
  if (cur.when || cur.body.some((l) => l.trim())) raw.push(cur);
  const entries = raw.map((r, i): NoteEntry => {
    const body = r.body.join('\n').trim();
    const report = /^Terminbericht\s*(\n|$)/.test(body);
    return {
      key: `${i}-${r.when}`,
      when: r.when,
      author: r.author || (r.when ? '' : 'Frühere Notizen'),
      appointment: r.appointment,
      report,
      blocks: noteBlocks(report ? body.replace(/^Terminbericht\s*\n?/, '') : body),
      length: body.length,
      duplicateOf: '',
    };
  });
  // Doppelt: Fast alle Wörter eines älteren, längeren Eintrags stehen auch in einem neueren.
  const sets = raw.map((r) => words(r.body.join(' ')));
  for (let i = 0; i < entries.length; i++) {
    const own = sets[i];
    const entry = entries[i];
    if (!own || !entry || own.size < 25) continue;
    for (let j = i + 1; j < entries.length; j++) {
      const other = sets[j];
      const newer = entries[j];
      if (!other || !newer) continue;
      let shared = 0;
      for (const w of own) if (other.has(w)) shared++;
      if (shared / own.size >= 0.85) {
        entry.duplicateOf = newer.when || 'später';
        break;
      }
    }
  }
  return entries.reverse();
}
